import { useMutation } from "@tanstack/react-query";

import { AuthContextType } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { getApiUrlOverride } from "@/utils/env";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { PLAYGROUND_SESSION_MODES, PlaygroundSessionModeType } from "@/components/Playground/types";

export const API_URL = getApiUrlOverride() || "https://api.promptlayer.com";

export const ENDPOINTS = {
  auth: {
    login: `${API_URL}/api/v2/auth/login`,
    logout: `${API_URL}/api/v2/auth/logout`,
    sso_callback: `${API_URL}/api/v2/auth/sso/callback`,
    social_authorize: `${API_URL}/api/v2/auth/social/authorize`,
    social_callback: `${API_URL}/api/v2/auth/social/callback`,
    validate_email: `${API_URL}/api/v2/auth/validate-email`,
    send_verification_email: `${API_URL}/api/v2/auth/send-verification-email`,
    verify_email_code: `${API_URL}/api/v2/auth/verify-email-code`,
    verify_oauth_email: `${API_URL}/api/v2/auth/verify-oauth-email`,
    signup: `${API_URL}/api/v2/auth/signup`,
    radar_attempt: `${API_URL}/api/v2/auth/radar-attempt`,
  },
  billing: {
    customer_portal: `${API_URL}/api/dashboard/v2/billing/customer-portal`,
    invoice_payment_link: `${API_URL}/api/dashboard/v2/billing/invoice-payment-link`,
    checkout_session: `${API_URL}/api/dashboard/v2/billing/checkout-session`,
    change_plan: `${API_URL}/api/dashboard/v2/billing/change-plan`,
    cancel_subscription: `${API_URL}/api/dashboard/v2/billing/cancel-subscription`,
    renew_subscription: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/renew-subscription`,
  },
  mfa: {
    enroll: `${API_URL}/api/dashboard/v2/mfa/enroll`,
    challenge: `${API_URL}/api/dashboard/v2/mfa/challenge`,
    verify: `${API_URL}/api/dashboard/v2/mfa/verify`,
    delete: `${API_URL}/api/dashboard/v2/mfa`,
    policy: `${API_URL}/api/dashboard/v2/mfa/policy`,
    break_glass_password: `${API_URL}/api/dashboard/v2/mfa/break-glass-password`,
  },
  add_request_log_to_dataset: `${API_URL}/add-request-log-to-dataset`,
  add_trace_to_dataset: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/dataset-versions/add-trace`,
  close_trace: (workspace_id: number, trace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/traces/${trace_id}/close`,
  traces_search: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/traces/search`,
  traces_entity_hydration: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/traces/entity-hydration`,
  get_workspaces: `${API_URL}/api/dashboard/v2/workspaces`,
  get_workspace_members: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/members`,
  workspace_members: `${API_URL}/api/dashboard/v2/workspace-members`,
  update_user: (user_id: number) => `${API_URL}/api/dashboard/v2/users/${user_id}`,
  delete_dataset_column: (workspace_id: number, dataset_group_id: number, dataset_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/dataset-groups/${dataset_group_id}/datasets/${dataset_id}/delete-column`,
  get_workflow_edges: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/edges`,
  get_api_keys: `${API_URL}/api-keys`,
  patch_dataset_group: (workspace_id: number, dataset_group_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/dataset-groups/${dataset_group_id}`,
  get_dataset_groups: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/dataset-groups`,
  admin_login: `${API_URL}/admin-login`,
  available_release_labels: `${API_URL}/available-release-labels`,
  available_tool_labels: `${API_URL}/available-tool-labels`,
  branch_prompt_template_version: `${API_URL}/branch-prompt-template-version`,
  create_account: `${API_URL}/create-account`,

  user_onboarding: `${API_URL}/user/onboarding`,
  user_onboarding_usecases: `${API_URL}/user/onboarding/usecases`,
  user_onboarding_usecases_generate: `${API_URL}/user/onboarding/usecases/generate`,
  user_onboarding_tutorial_status: `${API_URL}/user/onboarding/tutorial-status`,
  create_api_key: `${API_URL}/create-api-key`,
  global_model_configs: `${API_URL}/api/dashboard/v2/global/model-configs`,
  global_model_metadata: `${API_URL}/api/dashboard/v2/global/model-metadata`,
  models: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/model-configs`,
  edit_custom_model: (workspace_id: string, model_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/model-configs/${model_id}`,
  delete_custom_model: (workspace_id: string, model_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/model-configs/${model_id}`,
  global_providers: `${API_URL}/api/dashboard/v2/providers`,
  custom_models: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/model-configs-with-provider`,
  custom_model_prices: {
    list: (workspace_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/custom-model-prices`,
    item: (workspace_id: string, price_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/custom-model-prices/${price_id}`,
    suggestions: (workspace_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/custom-model-prices/suggestions`,
  },
  providers: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/providers`,
  recent_input_variables: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/recent-input-variables`,
  create_application: `${API_URL}/create-application`,
  create_individual_run: `${API_URL}/create-individual-run`,
  get_workspace_stats: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/stats`,
  create_stripe_checkout_session: `${API_URL}/create-stripe-checkout-session`,
  create_test_dataset: `${API_URL}/test-dataset-from-file`,
  dataset_column_to_json: `${API_URL}/dataset-column-to-json`,
  dataset_columns: `${API_URL}/dataset-columns`,
  dataset_groups: `${API_URL}/dataset-groups`,
  dataset_info: `${API_URL}/dataset-info`,
  dataset_rows: `${API_URL}/dataset-rows`,
  dataset_rows_from_filter_params: `${API_URL}/dataset-rows-from-filter-params`,
  datasets: `${API_URL}/datasets`,
  delete_api_key: `${API_URL}/delete-api-key`,
  delete_prompt_template: `${API_URL}/delete-prompt-template`,
  delete_user: `${API_URL}/delete-user`,
  draft_dataset_information: `${API_URL}/draft-dataset-information`,
  draft_dataset_state_from_dataset: `${API_URL}/draft-dataset-state-from-dataset`,
  duplicate_prompt_template_to_workspace: `${API_URL}/duplicate-prompt-template-to-workspace`,
  get_dynamic_release_labels_for_prompt: (workspace_id: number, prompt_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-registries/${prompt_id}/dynamic-release-labels`,
  get_reports: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/reports`,
  skill_collections: {
    list: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections`,
    get: (workspace_id: number, collection_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}`,
    files: (workspace_id: number, collection_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}/files`,
    versions: (workspace_id: number, collection_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}/versions`,
    version: (workspace_id: number, collection_id: string, version_number: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}/versions/${version_number}`,
    version_restore: (workspace_id: number, collection_id: string, version_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}/versions/${version_id}/restore`,
    version_archive: (workspace_id: number, collection_id: string, version_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}/versions/${version_id}/archive`,
    labels: (workspace_id: number, collection_id: string) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/skill-collections/${collection_id}/labels`,
    label: (label_id: number) => `${API_URL}/skill-collection-labels/${label_id}`,
  },
  tool_registry: {
    list: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry`,
    get: (workspace_id: number, tool_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}`,
    versions: (workspace_id: number, tool_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/versions`,
    version: (workspace_id: number, tool_id: number, version_number: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/versions/${version_number}`,
    version_archive: (workspace_id: number, tool_id: number, version_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/versions/${version_id}/archive`,
    version_test_execute: (workspace_id: number, tool_id: number, version_number: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/versions/${version_number}/test-execute`,
    labels: (workspace_id: number, tool_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/labels`,
    label: (label_id: number) => `${API_URL}/tool-registry-labels/${label_id}`,
    label_configuration: (label_id: number) =>
      `${API_URL}/tool-registry-labels/${label_id}/configuration`,
    references: (workspace_id: number, tool_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/references`,
  },
  mcp_servers: {
    list: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/mcp-servers`,
    get: (workspace_id: number, server_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/mcp-servers/${server_id}`,
    test: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/mcp-servers/test`,
  },
  entity_previews: {
    lookup: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/entity-previews/lookup`,
  },
  slack: {
    install: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/slack/install?workspace_id=${workspace_id}`,
    installation: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/slack/installation?workspace_id=${workspace_id}`,
    deleteInstallation: `${API_URL}/api/dashboard/v2/slack/installation`,
    linkInfo: (token: string) =>
      `${API_URL}/api/dashboard/v2/slack/link-info?token=${encodeURIComponent(token)}`,
    linkAccount: `${API_URL}/api/dashboard/v2/slack/link-account`,
  },
  env_vars: {
    workspace: {
      list: (workspace_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/env-vars`,
      create: (workspace_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/env-vars`,
      update: (workspace_id: number, var_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/env-vars/${var_id}`,
      delete: (workspace_id: number, var_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/env-vars/${var_id}`,
    },
    tool: {
      list: (workspace_id: number, tool_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/env-vars`,
      create: (workspace_id: number, tool_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/env-vars`,
      update: (workspace_id: number, tool_id: number, var_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/env-vars/${var_id}`,
      delete: (workspace_id: number, tool_id: number, var_id: number) =>
        `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tool-registry/${tool_id}/env-vars/${var_id}`,
    },
  },
  final_reports: (report_id: string) => `${API_URL}/reports/${report_id}/run`,
  folders: `${API_URL}/folders`,
  forgot_password: `${API_URL}/forgot-password`,
  get_group_requests: (workspace_id: number, group_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/groups/${group_id}/requests`,
  get_groups: (workspace_id: number, request_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests/${request_id}/groups`,
  get_intercom_verification: `${API_URL}/get-intercom-verification`,
  get_provider_api_key_status: `${API_URL}/get-provider-api-key-status`,
  get_request_counts_for_versions: `${API_URL}/get-request-counts-for-versions`,
  get_request_counts_for_workflows: (workspace_id: number, workflow_id: number) =>
    `${API_URL}/api/v2/dashboard/workspaces/${workspace_id}/workflows/${workflow_id}/request-counts`,
  get_request_stats_for_prompt: `${API_URL}/get-request-stats-for-prompt`,
  get_requests: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests`,
  get_request_search_suggestions: (
    workspace_id: number,
    field: string,
    options?: {
      prefix?: string;
      promptId?: number;
      metadataKey?: string;
      filterGroup?: string;
    },
  ) => {
    const url = `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/request/suggestions`;
    const params = new URLSearchParams({ field });
    if (options?.prefix) {
      params.set("prefix", options.prefix);
    }
    if (options?.promptId !== undefined) {
      params.set("prompt_id", String(options.promptId));
    }
    if (options?.metadataKey) {
      params.set("metadata_key", options.metadataKey);
    }
    if (options?.filterGroup) {
      params.set("filter_group", options.filterGroup);
    }
    return `${url}?${params.toString()}`;
  },
  get_structured_search_suggestions: (
    workspace_id: number,
    options: {
      searchType: string;
      source: string;
      field?: string;
      prefix?: string;
      promptId?: number;
      workflowId?: number;
      metadataKey?: string;
      filterGroup?: string;
      hideSignalMetadata?: boolean;
    },
  ) => {
    const url = `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/structured-search/suggestions`;
    const params = new URLSearchParams({
      search_type: options.searchType,
      source: options.source,
    });
    if (options.field) {
      params.set("field", options.field);
    }
    if (options.prefix) {
      params.set("prefix", options.prefix);
    }
    if (options.promptId !== undefined) {
      params.set("prompt_id", String(options.promptId));
    }
    if (options.workflowId !== undefined) {
      params.set("workflow_id", String(options.workflowId));
    }
    if (options.metadataKey) {
      params.set("metadata_key", options.metadataKey);
    }
    if (options.filterGroup) {
      params.set("filter_group", options.filterGroup);
    }
    if (options.hideSignalMetadata !== undefined) {
      params.set("hide_signal_metadata", String(options.hideSignalMetadata));
    }
    return `${url}?${params.toString()}`;
  },
  post_requests_analytics: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests/analytics`,
  post_request_analytics_custom_analytics: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests/analytics/custom-analytics`,
  post_trace_analytics_custom_analytics: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/traces/analytics/custom-analytics`,
  post_request_metadata_cost_breakdown: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests/analytics/metadata-cost-breakdown`,
  post_requests_structured_search: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests/search`,
  post_requests_hydrate: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/requests/hydrate`,
  signals: {
    list: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals`,
    create: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals`,
    get: (workspace_id: number, signal_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals/${signal_id}`,
    edit: (workspace_id: number, signal_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals/${signal_id}`,
    delete: (workspace_id: number, signal_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals/${signal_id}`,
    stats: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals/stats`,
    request_volume_tail: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals/request-volume-tail`,
    test: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/signals/test`,
  },
  get_requests_for_prompt: (workspace_id: number, prompt_id: string, version: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-registry-objects/${prompt_id}/prompt-versions/${version}/request-logs`,
  get_shared_request: `${API_URL}/get-shared-request`,
  get_user: `${API_URL}/get-user`,
  get_user_by_email: `${API_URL}/get-user-by-email`,
  individual_run_requests: `${API_URL}/individual-run-requests`,
  list_applications: `${API_URL}/list-applications`,
  list_users: `${API_URL}/list-users`,
  list_mcp_functions: `${API_URL}/list-mcp-functions`,
  login: `${API_URL}/login`,
  metadata_fields: `${API_URL}/metadata-fields`,
  span_metadata_keys: `${API_URL}/span-metadata-keys`,
  span_resource_keys: `${API_URL}/span-resource-keys`,
  move_prompt_template: `${API_URL}/move-prompt-template`,
  parse_input_variables: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-templates-input-variables`,
  prompt_labels: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-labels`,
  prompt_label_configuration: (label_id: number) =>
    `${API_URL}/prompt-labels/${label_id}/configuration`,
  legacy_prompt_labels: `${API_URL}/prompt-labels`,
  get_prompt_registry_objects: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-registry-objects`,
  prompt_registry_objects: `${API_URL}/prompt-registry-objects`,
  workspace_tags: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/tags`,
  prompt_templates: `${API_URL}/prompt-templates`,
  prompt_versions: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-versions`,
  legacy_prompt_versions: `${API_URL}/prompt-versions`,
  prompts: `${API_URL}/prompts`,
  get_prompt: `${API_URL}/api/v2/prompts`,
  release_label_groups: `${API_URL}/release-label-groups`,
  report_cells: (report_id: number) => `${API_URL}/reports/${report_id}/cells`,
  report_row_ids: (report_id: number) => `${API_URL}/reports/${report_id}/row_ids`,
  report_cell_cellId: (cell_id: number) => `${API_URL}/report-cells/${cell_id}`,
  report_cells_retry: (cell_id: number) => `${API_URL}/report-cells/${cell_id}/retry`,
  report_column_rerun: (report_column_id: number) =>
    `${API_URL}/report-columns/${report_column_id}/rerun`,
  report_columns: `${API_URL}/report-columns`,
  legacy_reports: `${API_URL}/reports`,
  duplicate_evaluation: (evaluation_id: number) =>
    `${API_URL}/api/dashboard/v2/evaluations/${evaluation_id}/duplicate`,
  get_datasets: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/datasets`,
  request_stats_by_metadata_field: `${API_URL}/request-stats-by-metadata-field`,
  request_stats_by_prompt: `${API_URL}/request-stats-by-prompt`,
  request_stats_by_tag: `${API_URL}/request-stats-by-tag`,
  reset_password: `${API_URL}/reset-password`,
  save_dataset: `${API_URL}/save-dataset`,
  score_names: `${API_URL}/score-names`,
  set_or_remove_azure_keys: `${API_URL}/set-or-remove-azure-keys`,
  set_or_remove_vertexai_keys: `${API_URL}/set-or-remove-vertexai-keys`,
  set_or_remove_bedrock_keys: `${API_URL}/set-or-remove-bedrock-keys`,
  set_or_remove_provider_api_key: `${API_URL}/set-or-remove-provider-api-key`,
  set_user_to_free_plan: `${API_URL}/set-user-to-free-plan`,
  spans: `${API_URL}/spans`,
  statistics_page: `${API_URL}/statistics-page`,
  tags: `${API_URL}/tags`,
  toggle_sharable_request: `${API_URL}/toggle-sharable-request`,
  toggle_starred_request: `${API_URL}/toggle-starred-request`,
  trace: `${API_URL}/trace`,
  track_metadata: `${API_URL}/rest/track-metadata`,
  track_score: `${API_URL}/track-score`,
  update_tags: `${API_URL}/update-tags`,
  upload: `${API_URL}/upload`,
  usage: `${API_URL}/usage`,
  webhooks: `${API_URL}/webhooks`,
  legacy_workspace_members: `${API_URL}/workspace-members`,
  legacy_workspaces: `${API_URL}/workspaces`,
  ws_token_request: `${API_URL}/ws-token-request`,
  backtests: `${API_URL}/backtests`,
  ab_tests: `${API_URL}/ab-tests`,
  verify_dynamic_release_label_existence: `${API_URL}/verify-dynamic-release-label-existence`,
  file_upload_multipart: `${API_URL}/file-upload-multipart`,
  workflow_version_detail: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/workflow-version-detail`,
  workflow_nodes: `${API_URL}/workflow-nodes`,
  workflow_version_executions: `${API_URL}/workflow-version-executions`,
  workflow_versions: `${API_URL}/workflow-versions`,
  workflows: `${API_URL}/api/dashboard/v2/workflows`,
  workflow_editor_state: `${API_URL}/workflow-editor-state`,
  workflow_play_from_here: `${API_URL}/workflow-play-from-here`,
  workflow_labels: `${API_URL}/workflow-labels`,
  workflow_label_configuration: (label_id: number) =>
    `${API_URL}/workflow-labels/${label_id}/configuration`,
  available_workflow_release_labels: `${API_URL}/available-workflow-release-labels`,
  workflow_release_label_groups: `${API_URL}/workflow-release-label-groups`,
  workflow_editor_input_variables: `${API_URL}/workflow-editor-input-variables`,
  inference_clients: `${API_URL}/inference_clients`,
  search_dataset_rows: `${API_URL}/search-dataset-rows`,
  create_dataset_from_evaluation: `${API_URL}/create-dataset-from-evaluation`,
  new_requests: `${API_URL}/new-requests`,
  new_spans: `${API_URL}/new-spans`,
  report_columns_include_prompt_template_latest_version: `${API_URL}/report-columns-include-prompt-template-latest-version`,
  request_logs: `${API_URL}/request-logs`,
  request_logs_bulk: `${API_URL}/request-logs-bulk`,
  vector_store_file_content: `${API_URL}/api/dashboard/v2/vector-store/file/content`,
  output_media_content: `${API_URL}/api/dashboard/v2/output-media/content`,
  get_prompt_version_scores: (workspace_id: number, prompt_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-templates/${prompt_id}/version-evaluation-scores`,
  request_log_input_variables: (id: number) => `${API_URL}/request-log/${id}/input-variables`,
  run_groups: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/run_groups`,
  cancel_playground_runs: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_runs/cancel`,
  playground_sessions: (
    workspace_id: number,
    session_id?: string,
    config?: {
      mode?: PlaygroundSessionModeType;
    },
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions${session_id ? `/${session_id}` : ""}${config?.mode && config.mode === PLAYGROUND_SESSION_MODES.CHAT ? `?mode=${config.mode}` : ""}`,
  playground_evaluate: (workspace_id: number, session_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate`,
  playground_evaluate_settings: (workspace_id: number, session_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/settings`,
  playground_evaluate_draft: (workspace_id: number, session_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/draft`,
  playground_evaluate_sheet_references: (
    workspace_id: number,
    session_id: string,
    reference_id?: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/sheet_references${reference_id ? `/${reference_id}` : ""}`,
  playground_evaluate_sheet_reference_runs: (
    workspace_id: number,
    session_id: string,
    reference_id: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/sheet_references/${reference_id}/runs`,
  playground_evaluate_sheet_reference_promote: (
    workspace_id: number,
    session_id: string,
    reference_id: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/sheet_references/${reference_id}/promote`,
  playground_evaluate_sheet_reference_export: (
    workspace_id: number,
    session_id: string,
    reference_id: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/sheet_references/${reference_id}/export`,
  playground_evaluate_runs: (workspace_id: number, session_id: string, run_id?: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/runs${run_id ? `/${run_id}` : ""}`,
  playground_evaluate_run_cancel: (workspace_id: number, session_id: string, run_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/evaluate/runs/${run_id}/cancel`,

  playground_session_chat_messages: (workspace_id: number, session_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/chat_messages`,
  playground_session_chat_message: (workspace_id: number, session_id: string, message_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/chat_messages/${message_id}`,
  playground_session_chat_message_replay: (
    workspace_id: number,
    session_id: string,
    message_id: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/chat_messages/${message_id}/replay`,
  reset_chat_from_request: (workspace_id: number, session_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/${session_id}/reset_chat_from_request`,
  generate_playground_session_name: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/playground_sessions/generate_name`,
  workflow_version_execution_results: () => `${API_URL}/workflow-version-execution-results`,
  organizations: `${API_URL}/api/dashboard/v2/organizations`,
  create_organization_with_workspace_and_invites: `${API_URL}/api/dashboard/v2/organizations-with-workspace-and-invites`,
  me: `${API_URL}/api/dashboard/v2/users/me`,
  workspaces: `${API_URL}/api/dashboard/v2/workspaces`,
  organization_members: `${API_URL}/api/dashboard/v2/organization-members`,
  workspace_invites: `${API_URL}/api/dashboard/v2/workspace-invites`,
  organization_roles: `${API_URL}/api/dashboard/v2/organization-roles`,
  get_organization_roles: (organization_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/roles`,
  create_organization_role: (organization_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/roles`,
  update_organization_role: (organization_id: number, role_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/roles/${role_id}`,
  delete_organization_role: (organization_id: number, role_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/roles/${role_id}`,
  get_workspace_member_roles: (workspace_id: number, member_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/members/${member_id}/roles`,
  update_workspace_member_roles: (workspace_id: number, member_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/members/${member_id}/roles`,
  metadata_field_values: (workspace_id: number, metadata_field_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/metadata-fields/${metadata_field_id}/values`,
  expand_prompt_blueprint: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/expand-prompt-blueprint`,
  resolve_workflow_name: (workspace_id: number, workflow_name: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/workflows/resolve-name/${workflow_name}`,
  get_workflow: `${API_URL}/api/v2/workflows`,
  get_workflow_release_labels: (workspace_id: number, workflow_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/workflows/${workflow_id}/release-labels`,
  folder_entities: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/folders/entities`,
  embedding_count: (workspace_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/embedding-count`,
  get_invitee: `${API_URL}/api/dashboard/v2/get-invitee`,
  create_account_from_invite: `${API_URL}/api/dashboard/v2/create-account-from-invite`,
  test_run_workflow: (workspace_id: number, workflow_name: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/workflows/${encodeURIComponent(
      workflow_name,
    )}/test-run`,
  get_workflow_execution_results: `${API_URL}/api/dashboard/v2/workflow-version-execution-results`,
  set_default_organization: `${API_URL}/api/dashboard/v2/users/default-organization`,
  set_default_workspace: `${API_URL}/api/dashboard/v2/users/default-workspace`,
  export_dataset_to_csv: (workspace_id: number, dataset_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/datasets/${dataset_id}/export-dataset-to-csv`,
  get_workflow_version_stats: (workspace_id: number, workflow_id: number, version: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/workflows/${workflow_id}/versions/${version}/stats`,
  input_variable_sets: (workspace_id: number, id?: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/input-variable-sets${
      id ? `/${id}` : ""
    }`,
  verify_release_label_existence: `${API_URL}/verify-release-label-existence`,
  recent_playground_sessions: (workspaceId: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/recent-playground-sessions`,
  organization_deployments_usage: (organization_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/deployments-usage`,
  organization_usage_workspaces_usage: (organization_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/workspaces-usage`,
  organization_subscription: (organization_id: number) =>
    `${API_URL}/api/dashboard/v2/organizations/${organization_id}/subscription`,
  get_user_permissions: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/permissions`,
  changelog_comments: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/changelog-comments`,
  changelog_comment: (workspace_id: number, comment_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/changelog-comments/${comment_id}`,
  changelog_comment_replies: (workspace_id: number, comment_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/changelog-comments/${comment_id}/replies`,
  get_notifications: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/notifications`,
  clear_notifications: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/notifications`,
  dismiss_notification: (workspace_id: number, notification_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/notifications/${notification_id}`,
  vibe_chat: `${API_URL}/api/dashboard/v2/vibe-chat`,
  recent_vibe_chat_sessions: `${API_URL}/api/dashboard/v2/recent-vibe-chat-sessions`,
  delete_vibe_chat: `${API_URL}/api/dashboard/v2/vibe-chat`,
  delete_all_vibe_chat: `${API_URL}/api/dashboard/v2/vibe-chat/all`,
  cancel_vibe_chat: `${API_URL}/api/dashboard/v2/vibe-chat/cancel`,
  prompt_stars: (workspace_id: number, prompt_registry_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/prompt-registry/${prompt_registry_id}/stars`,
  starred_prompts: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/starred-prompts`,
  favorites: (workspace_id: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/favorites`,
  favorite_stars: (workspace_id: number, entity_type: string, entity_id: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/favorites/${entity_type}/${entity_id}/stars`,
  // Tables
  smart_tables: (wid: number) => `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables`,
  smart_table_legacy_migrations: (wid: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/legacy-migrations`,
  smart_table_model_comparison_preset: (wid: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/presets/model-comparison`,
  smart_table_evaluation_preset: (wid: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/presets/evaluation`,
  smart_table_custom_preset: (wid: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/presets/custom`,
  smart_table_brand_visibility_preset: (wid: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/presets/brand-visibility`,
  smart_table_sheet_model_comparison_preset: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/presets/model-comparison`,
  smart_table_sheet_evaluation_preset: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/presets/evaluation`,
  smart_table_sheet_custom_preset: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/presets/custom`,
  smart_table_sheet_brand_visibility_preset: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/presets/brand-visibility`,
  smart_table: (wid: number, tid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}`,
  smart_table_duplicate: (wid: number, tid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/duplicate`,
  smart_table_sheets: (wid: number, tid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets`,
  smart_table_sheet: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}`,
  smart_table_sheet_status_counts: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/status-counts`,
  smart_table_sheet_cancel: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cancel`,
  smart_table_grid: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/grid`,
  smart_table_columns: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns`,
  smart_table_imported_source_ids: (
    wid: number,
    tid: string,
    sid: string,
    params: { source: "request" | "trace"; columnId?: string },
  ) => {
    const search = new URLSearchParams({ source: params.source });
    if (params.columnId) {
      search.set("column_id", params.columnId);
    }
    return `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/imported-source-ids?${search.toString()}`;
  },
  smart_table_column: (wid: number, tid: string, sid: string, cid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}`,
  smart_table_column_move: (wid: number, tid: string, sid: string, cid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}/move`,
  smart_table_column_duplicate: (wid: number, tid: string, sid: string, cid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}/duplicate`,
  smart_table_column_dependencies: (wid: number, tid: string, sid: string, cid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}/dependencies`,
  smart_table_available_sources: (wid: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/columns/available-sources`,
  smart_table_column_recalculate: (wid: number, tid: string, sid: string, cid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}/recalculate`,
  smart_table_column_cancel: (wid: number, tid: string, sid: string, cid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}/cancel`,
  smart_table_convert_composition_column_to_text: (
    wid: number,
    tid: string,
    sid: string,
    cid: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/columns/${cid}/convert-composition-to-text`,
  smart_table_cells: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells`,
  smart_table_cells_batch_text: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/batch-text`,
  smart_table_cell: (wid: number, tid: string, sid: string, cellId: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/${cellId}`,
  smart_table_cell_import: (wid: number, tid: string, sid: string, cellId: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/${cellId}/import`,
  smart_table_virtual_cell: (
    wid: number,
    tid: string,
    sid: string,
    coordinates: { columnId: string; rowIndex: number },
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/virtual/${coordinates.columnId}/${coordinates.rowIndex}`,
  smart_table_cell_recalculate: (wid: number, tid: string, sid: string, cellId: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/${cellId}/recalculate`,
  smart_table_cell_cancel: (wid: number, tid: string, sid: string, cellId: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/${cellId}/cancel`,
  smart_table_cells_batch_recalculate: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/cells/batch-recalculate`,
  smart_table_rows: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/rows`,
  smart_table_rows_cancel: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/rows/cancel`,
  smart_table_rows_copy: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/rows/copy`,
  smart_table_compute_confirm: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/compute/confirm`,
  smart_table_compute_batch_recalculate: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/compute/batch-recalculate`,
  smart_table_debug_reset_pending: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/debug/reset-pending`,
  smart_table_debug_mark_completed: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/debug/mark-completed`,
  smart_table_debug_status: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/debug/status`,
  smart_table_score: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/score`,
  smart_table_configure_score: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/configure-score`,
  smart_table_scorecard: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard`,
  smart_table_scorecard_cancel: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/cancel`,
  smart_table_scorecard_migrate_legacy_score: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/migrate-legacy-score`,
  smart_table_scorecard_recalculate: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/recalculate`,
  smart_table_scorecard_rows: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/rows`,
  smart_table_scorecard_row: (wid: number, tid: string, sid: string, rowIndex: number) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/rows/${rowIndex}`,
  smart_table_scorecard_preview: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/preview`,
  smart_table_scorecard_preview_item: (wid: number, tid: string, sid: string, previewId: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/scorecard/preview/${previewId}`,
  smart_table_versions: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/versions`,
  smart_table_version_score_history: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/versions/score-history`,
  smart_table_version: (wid: number, tid: string, sid: string, versionId: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/versions/${versionId}`,
  smart_table_version_cell: (
    wid: number,
    tid: string,
    sid: string,
    target: { versionId: string; columnId: string; rowIndex: number },
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/versions/${target.versionId}/cells/${target.columnId}/${target.rowIndex}`,
  smart_table_csv_imports: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-imports`,
  smart_table_csv_imports_direct_upload: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-imports/direct-upload`,
  smart_table_csv_imports_direct_upload_complete: (
    wid: number,
    tid: string,
    sid: string,
    operationId: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-imports/direct-upload/${operationId}/complete`,
  smart_table_csv_imports_direct_upload_heartbeat: (
    wid: number,
    tid: string,
    sid: string,
    operationId: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-imports/direct-upload/${operationId}/heartbeat`,
  smart_table_csv_imports_direct_upload_cancel: (
    wid: number,
    tid: string,
    sid: string,
    operationId: string,
  ) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-imports/direct-upload/${operationId}/cancel`,
  smart_table_csv_exports: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-exports`,
  smart_table_csv_operations: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/csv-operations`,
  smart_table_request_imports: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/request-imports`,
  smart_table_trace_imports: (wid: number, tid: string, sid: string) =>
    `${API_URL}/api/dashboard/v2/workspaces/${wid}/smart-tables/${tid}/sheets/${sid}/trace-imports`,
  identity: {
    settings: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity`,
    admin_portal_link: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/admin-portal-link`,
    groups: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/groups`,
    group_mappings: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/group-mappings`,
    group_mapping: (organization_id: number, mapping_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/group-mappings/${mapping_id}`,
    audit_events: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/audit-events`,
    audit_events_csv: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/audit-events.csv`,
    break_glass: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/break-glass`,
    test_sso: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/test-sso`,
    sync_workos: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/sync-workos`,
    workos_organizations: (organization_id: number) =>
      `${API_URL}/api/dashboard/v2/organizations/${organization_id}/identity/workos-organizations`,
    no_workspace_access_context: `${API_URL}/api/dashboard/v2/identity/no-workspace-access-context`,
    workspace_fallback_owner: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/fallback-owner`,
    workspace_roles: (workspace_id: number) =>
      `${API_URL}/api/dashboard/v2/workspaces/${workspace_id}/roles`,
  },
};

export const adminLogin = async ({
  admin_email,
  password,
  target_user_email,
}: {
  admin_email: string;
  password: string;
  target_user_email: string;
}): Promise<Response> =>
  fetch(ENDPOINTS.admin_login, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      admin_email,
      password,
      target_user_email,
    }),
  });

export const useCreateAccount = () =>
  useMutation({
    mutationFn: async ({
      email,
      password,
      name,
      recaptcha_response,
      gclid,
    }: {
      email: string;
      password: string;
      name: string;
      recaptcha_response: string;
      gclid?: string;
    }) =>
      fetch(ENDPOINTS.create_account, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          name,
          recaptcha_response,
          gclid,
        }),
      }).then(handleResponse),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      displayErrorToast(
        typeof error === "string"
          ? error
          : "Unexpected server response format. Please try again later.",
      );
    },
  });

export const generateNewApiKey = (
  authContext: AuthContextType,
  workspaceId: number,
  name?: string,
) => {
  if (!authContext) {
    return Promise.resolve(new Response(null, { status: 401, statusText: "Unauthorized" }));
  }

  const { userToken } = authContext;

  return fetch(ENDPOINTS.create_api_key, {
    method: "POST",
    headers: jsonAuthHeaders(userToken!),
    body: JSON.stringify({
      workspace_id: workspaceId,
      name: name || null,
    }),
  });
};

export const useLogin = () =>
  useMutation({
    mutationFn: async ({
      email,
      password,
      recaptcha_response,
    }: {
      email: string;
      password: string;
      recaptcha_response?: string;
    }) =>
      fetch(ENDPOINTS.login, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          recaptcha_response,
        }),
        credentials: "include",
      }).then(handleResponse),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      displayErrorToast(
        typeof error === "string"
          ? error
          : "Unexpected server response format. Please try again later.",
      );
    },
  });

export const useForgotPassword = () =>
  useMutation({
    mutationFn: ({ email }: { email: string }) =>
      fetch(ENDPOINTS.forgot_password, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
        }),
      }).then(handleResponse),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (data: any) => {
      displayToast(data.message, ToastType.success);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      displayErrorToast(
        typeof error === "string"
          ? error
          : "Unexpected server response format. Please try again later.",
      );
    },
  });

export const useResetPassword = () =>
  useMutation({
    mutationFn: async ({
      code,
      email,
      password,
    }: {
      code: string;
      email: string;
      password: string;
    }) =>
      fetch(ENDPOINTS.reset_password, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          email,
          password,
        }),
      }).then(handleResponse),
  });

export const toggleSharableRequest = (
  authContext: AuthContextType,
  id: number,
  newVal: boolean,
) => {
  const { userToken } = authContext;
  return fetch(ENDPOINTS.toggle_sharable_request, {
    method: "POST",
    headers: jsonAuthHeaders(userToken!),
    body: JSON.stringify({
      request_id: id,
      new_value: newVal,
    }),
  });
};
